"use client";

import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { LGSHeader } from "modules/lgs/components/stores/LGSHeader";
import LGSTradesItemsView from "modules/lgs/components/stores/table/LGSTradesItemsView";
import { TradeListType } from "modules/tradeLists/model/tradeList";
import { LGS } from "modules/lgs/model/lgs";
import { useInfiniteLGSCards } from "modules/lgs/hooks/useInfiniteLGSCards";
import { useLGSUnifiedFilters } from "modules/lgs/hooks/useLGSUnifiedFilters";
import TradeSectionTitle from "modules/lgs/components/stores/table/TradeSectionTitle";
import { useInfiniteLGSMatchedCards } from "modules/lgs/hooks/useInfiniteMatchedCards";
import { LGSHeaderTabs } from "modules/lgs/components/stores/tabs/LGSHeaderTabs";
import { ShortLGS } from "modules/common/model/models";
import { useAuth } from "@clerk/nextjs";
import { EmptyStateCard } from "modules/common/components/EmptyStateCard";
import { useQuickJoinStore } from "modules/lgs/hooks/useQuickJoinStore";
import { LGSMobileTabs } from "modules/lgs/components/stores/mobile/LGSMobileTabs";
import { cn } from "utils/utils";
import { LGSStoreSidebar } from "modules/lgs/components/stores/LGSStoreSidebar";
import { ArrowRight, SwatchBook } from "lucide-react";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { useMediaQuery } from "modules/common/hooks/useMediaQuery";
import { VisitorCtaBar } from "modules/common/components/funnels/VisitorCtaBar";

interface LGSContentProps {
  lgs: LGS;
  userLGSs?: ShortLGS[];
  currentSlug?: string;
  cityName?: string | null;
}

export function LGSContent({
  lgs,
  userLGSs,
  currentSlug,
  cityName,
}: LGSContentProps) {
  const { isSignedIn, isLoaded } = useAuth();
  const pathname = usePathname();
  const pathPrefix = pathname?.startsWith("/x/") ? "/x/stores" : "/stores";
  const isMobile = useMediaQuery("(max-width: 767px)");
  const activeTab = pathname?.includes("/wants")
    ? "wants"
    : pathname?.includes("/matched")
      ? "matched"
      : "trades";

  // Smart CTA: check membership for join button
  const { isMember, joinStore, isJoining } = useQuickJoinStore(lgs.wotcId, {
    storeCoords: [lgs.longitude, lgs.latitude],
  });

  // Use unified filters that affect all sections (matched, trades, wants)
  const {
    tradesFilters: lgsCardsReqTrades,
    wantsFilters: lgsCardsReqWants,
    matchedFilters: lgsMatchedCardsReq,
    activeView: unifiedActiveView,
    hasActiveFilters,
  } = useLGSUnifiedFilters(lgs.wotcId, "");
  const {
    data: dataResultsTrades,
    totalResults: totalResultsTrades,
    isFetching: isFetchingTrades,
    isPending: isPendingTrades,
    hasNextPage: hasNextPageTrades,
    isFetchingNextPage: isFetchingNextPageTrades,
    fetchNextPage: fetchNextPageTrades,
  } = useInfiniteLGSCards(
    {
      ...lgsCardsReqTrades,
      id: lgs.wotcId,
      type: TradeListType.TRADES,
    },
    { enabled: isMobile && activeTab === "trades" }
  );

  const {
    data: dataResultsWants,
    totalResults: totalResultsWants,
    isFetching: isFetchingWants,
    isPending: isPendingWants,
    hasNextPage: hasNextPageWants,
    isFetchingNextPage: isFetchingNextPageWants,
    fetchNextPage: fetchNextPageWants,
  } = useInfiniteLGSCards(
    {
      ...lgsCardsReqWants,
      id: lgs.wotcId,
      type: TradeListType.WANTS,
    },
    { enabled: isMobile && activeTab === "wants" }
  );

  const {
    data: dataResultsMatched,
    totalResults: totalResultsMatched,
    isFetching: isFetchingMatched,
    isPending: isPendingMatched,
    hasNextPage: hasNextPageMatched,
    isFetchingNextPage: isFetchingNextPageMatched,
    fetchNextPage: fetchNextPageMatched,
  } = useInfiniteLGSMatchedCards(lgsMatchedCardsReq, {
    enabled: isMobile && activeTab === "matched",
  });

  // Helper function to check if data is empty
  const isDataEmpty = (data: any) => {
    if (!data || !data.pages || data.pages.length === 0) return true;

    // Check if there are any items in the first page
    const firstPage = data.pages[0];
    return !firstPage || !firstPage.items || firstPage.items.length === 0;
  };

  // Only consider isPending as "has data" if user is signed in (for matched) or query is actually running
  const hasMatchedData =
    !isDataEmpty(dataResultsMatched) || (isSignedIn && isPendingMatched);
  const hasTradesData = !isDataEmpty(dataResultsTrades) || isPendingTrades;
  const hasWantsData = !isDataEmpty(dataResultsWants) || isPendingWants;

  // Empty state messages based on filter state and total results
  // Prioritize showing filter message when filters are active
  const getEmptyMessage = (
    type: "matched" | "trades" | "wants",
    totalResults: number | undefined
  ) => {
    // If filters are active and returned nothing, show filter message
    if (hasActiveFilters) {
      return "No cards match your current filters. Try adjusting your search or filters.";
    }

    // If there are zero cards total, show the "no cards added" message
    if (totalResults === 0) {
      switch (type) {
        case "matched":
          return isSignedIn
            ? "No matched cards found. Cards matching your wants and other users' trades will appear here."
            : `Please sign in to trade at ${lgs.name}.`;
        case "trades":
          return "No cards for trade have been added to this store yet.";
        case "wants":
          return "No wanted cards have been added to this store yet.";
      }
    }

    // Fallback to default messages
    switch (type) {
      case "matched":
        return isSignedIn
          ? "No matched cards found. Cards matching your wants and other users' trades will appear here."
          : `Please sign in to trade at ${lgs.name}.`;
      case "trades":
        return "No cards for trade have been added to this store yet.";
      case "wants":
        return "No wanted cards have been added to this store yet.";
    }
  };

  // Common section render function to avoid duplication
  const renderSection = (
    type: "matched" | "trades" | "wants",
    className?: string,
    maxItems?: number
  ) => {
    const sectionProps = {
      matched: {
        data: dataResultsMatched,
        isPending: isPendingMatched,
        hasNextPage: hasNextPageMatched,
        isFetchingNextPage: isFetchingNextPageMatched,
        fetchNextPage: fetchNextPageMatched,
        isFetching: isFetchingMatched,
        totalResults: totalResultsMatched,
        currentCount:
          dataResultsMatched?.pages.flatMap((page) => page.items).length || 0,
        title: "Matches",
        description:
          "Cards where your haves and wants overlap with other traders",
        hasData: hasMatchedData,
        emptyMessage: getEmptyMessage("matched", totalResultsMatched),
        isMatchedTrades: true,
      },
      trades: {
        data: dataResultsTrades,
        isPending: isPendingTrades,
        hasNextPage: hasNextPageTrades,
        isFetchingNextPage: isFetchingNextPageTrades,
        fetchNextPage: fetchNextPageTrades,
        isFetching: isFetchingTrades,
        totalResults: totalResultsTrades,
        currentCount:
          dataResultsTrades?.pages.flatMap((page) => page.items).length || 0,
        title: "Haves",
        description: "Cards available for trade at this store",
        hasData: hasTradesData,
        emptyMessage: getEmptyMessage("trades", totalResultsTrades),
        isMatchedTrades: false,
      },
      wants: {
        data: dataResultsWants,
        isPending: isPendingWants,
        hasNextPage: hasNextPageWants,
        isFetchingNextPage: isFetchingNextPageWants,
        fetchNextPage: fetchNextPageWants,
        isFetching: isFetchingWants,
        totalResults: totalResultsWants,
        currentCount:
          dataResultsWants?.pages.flatMap((page) => page.items).length || 0,
        title: "Wants",
        description: "Cards players are looking for at this store",
        hasData: hasWantsData,
        emptyMessage: getEmptyMessage("wants", totalResultsWants),
        isMatchedTrades: false,
      },
    }[type];

    const viewAllLinkMap = {
      matched: `${pathPrefix}/${lgs.slug}/matched`,
      trades: `${pathPrefix}/${lgs.slug}/haves`,
      wants: `${pathPrefix}/${lgs.slug}/wants`,
    };
    const viewAllLink = viewAllLinkMap[type];
    const total = sectionProps.totalResults ?? 0;
    const hasAdditionalItems =
      type === "matched" ? sectionProps.hasNextPage : total > (maxItems ?? 0);
    const viewAllLabel =
      type === "matched" ? "View all" : `View all ${total} cards`;

    return (
      <div className={cn("flex h-full flex-col", className)}>
        <TradeSectionTitle
          currentCount={
            type === "matched" ? undefined : sectionProps.currentCount
          }
          total={type === "matched" ? undefined : sectionProps.totalResults}
          title={sectionProps.title}
          description={maxItems ? sectionProps.description : undefined}
          type={type === "matched" ? "match" : type}
          viewAllLink={maxItems && hasAdditionalItems ? viewAllLink : undefined}
          sticky={!!maxItems}
        />
        <div className="flex-1 overflow-hidden">
          {sectionProps.hasData ? (
            <LGSTradesItemsView
              data={sectionProps.data}
              isPending={sectionProps.isPending}
              hasNextPage={sectionProps.hasNextPage}
              isFetchingNextPage={sectionProps.isFetchingNextPage}
              fetchNextPage={sectionProps.fetchNextPage}
              isMatchedTrades={sectionProps.isMatchedTrades}
              activeView={unifiedActiveView}
              maxItems={maxItems}
            />
          ) : isLoaded && !isSignedIn ? (
            <EmptyStateCard
              variant="signed-out"
              icon={<SwatchBook className="rotate-135 text-primary h-8 w-8" />}
              title="Sign up"
              description={sectionProps.emptyMessage}
            />
          ) : (
            <EmptyStateCard
              variant="compact"
              icon={<SwatchBook className="rotate-135 text-primary h-8 w-8" />}
              title={
                hasActiveFilters ? "No Results" : sectionProps.emptyMessage
              }
              description={sectionProps.emptyMessage}
              ctaText={
                hasActiveFilters
                  ? undefined
                  : !isMember
                    ? `Join ${lgs.name}`
                    : "Create List"
              }
              ctaLink={hasActiveFilters || !isMember ? undefined : "/x/lists"}
              onCtaClick={
                !hasActiveFilters && !isMember ? () => joinStore() : undefined
              }
              ctaLoading={isJoining}
            />
          )}
        </div>
        {maxItems && hasAdditionalItems && (
          <div className="flex justify-center pb-1 pt-3">
            <Link
              href={viewAllLink}
              className="btn btn-soft btn-sm btn-primary gap-1"
            >
              {viewAllLabel}
              <ArrowRight size={ICON_SIZE} />
            </Link>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Render tabs navigation if we have multiple stores */}
      {userLGSs && (
        <LGSHeaderTabs userLgs={userLGSs} currentSlug={currentSlug} />
      )}
      {/* Mobile: Store info - above all content */}
      <div className="md:hidden">
        <LGSHeader lgs={lgs} cityName={cityName} />
      </div>

      {/* Mobile Layout: Tabbed sections */}
      {/* The store hub's lists sit in normal flow (no DataViewPanel), so they
          need the fixed dock+CTA stack's height as bottom padding. */}
      <div className="relative flex flex-1 flex-col gap-2 pb-[var(--bottom-bar,0px)] md:hidden">
        <LGSMobileTabs
          lgsId={lgs.wotcId}
          slug={lgs.slug}
          matchedSection={{
            data: dataResultsMatched,
            isPending: isPendingMatched,
            hasNextPage: hasNextPageMatched,
            isFetchingNextPage: isFetchingNextPageMatched,
            fetchNextPage: fetchNextPageMatched,
            isFetching: isFetchingMatched,
            totalResults: totalResultsMatched,
            hasData: !!hasMatchedData,
            isMatchedTrades: true,
          }}
          tradesSection={{
            data: dataResultsTrades,
            isPending: isPendingTrades,
            hasNextPage: hasNextPageTrades,
            isFetchingNextPage: isFetchingNextPageTrades,
            fetchNextPage: fetchNextPageTrades,
            isFetching: isFetchingTrades,
            totalResults: totalResultsTrades,
            hasData: hasTradesData,
            isMatchedTrades: false,
          }}
          wantsSection={{
            data: dataResultsWants,
            isPending: isPendingWants,
            hasNextPage: hasNextPageWants,
            isFetchingNextPage: isFetchingNextPageWants,
            fetchNextPage: fetchNextPageWants,
            isFetching: isFetchingWants,
            totalResults: totalResultsWants,
            hasData: hasWantsData,
            isMatchedTrades: false,
          }}
          activeView={unifiedActiveView}
          hasActiveFilters={hasActiveFilters}
          isSignedIn={!!isSignedIn}
          isAuthLoaded={isLoaded}
          cta={
            <VisitorCtaBar
              variant="stacked"
              context={{
                kind: "store",
                direction: "haves",
                storeName: lgs.name,
              }}
            />
          }
        />
      </div>

      {/* Desktop Layout: Main content + Sidebar */}
      <div className="hidden gap-6 md:grid md:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-4">
          <LGSHeader lgs={lgs} cityName={cityName} />
          {renderSection("matched", undefined, 8)}
          {renderSection("trades", undefined, 8)}
          {renderSection("wants", undefined, 8)}
        </div>
        <aside>
          <LGSStoreSidebar lgs={lgs} cityName={cityName} />
        </aside>
      </div>
    </>
  );
}
